import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, Card, Button, Badge, LoadingState, Alert } from '../components/ui';
import sessionService from '../services/sessionService';
import accountService from '../services/accountService';
import { 
  ServerIcon, 
  ExclamationCircleIcon, 
  CheckCircleIcon,
  ArrowLeftIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const SessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Fetch session details
  const { 
    data: session, 
    isLoading: sessionLoading, 
    error: sessionError 
  } = useQuery(
    ['session', id], 
    () => sessionService.getSession(id),
    {
      retry: 1,
      onError: (err) => {
        console.error('Error fetching session:', err);
      }
    }
  );
  
  // Fetch account details
  const { 
    data: account, 
    isLoading: accountLoading, 
    error: accountError 
  } = useQuery(
    ['account', id], 
    () => accountService.getAccount(id),
    {
      retry: 1,
      onError: (err) => {
        console.error('Error fetching account:', err);
      }
    }
  );
  
  // Delete session mutation
  const deleteSessionMutation = useMutation(
    () => sessionService.deleteSession(id),
    {
      onSuccess: () => {
        setSuccess('Session files deleted successfully');
        queryClient.invalidateQueries(['session', id]);
      },
      onError: (err) => {
        setError(`Failed to delete session: ${err.message}`);
      }
    }
  );
  
  // Reset session mutation
  const resetSessionMutation = useMutation(
    () => sessionService.resetSession(id),
    {
      onSuccess: () => {
        setSuccess('Session reset successfully');
        queryClient.invalidateQueries(['session', id]);
        queryClient.invalidateQueries(['account', id]);
      },
      onError: (err) => {
        setError(`Failed to reset session: ${err.message}`);
      }
    }
  );
  
  // Handle delete session
  const handleDeleteSession = () => {
    if (window.confirm('Are you sure you want to delete this session? This will only delete the session files, not the account.')) {
      deleteSessionMutation.mutate();
    }
  };
  
  // Handle reset session
  const handleResetSession = () => {
    if (window.confirm('Are you sure you want to reset this session? This will delete the session files and update the account status. The account will need to be re-authenticated.')) {
      resetSessionMutation.mutate();
    }
  };
  
  // Loading state
  if (sessionLoading || accountLoading) {
    return <LoadingState message="Loading session details..." />;
  }
  
  // Error state
  if (sessionError || accountError) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          {sessionError?.message || accountError?.message || 'Failed to load session details'}
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/sessions')}
        >
          Back to Sessions
        </Button>
      </div>
    );
  }
  
  // Not found state
  if (!session || !account) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          Session or account not found
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/sessions')}
        >
          Back to Sessions
        </Button>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader 
        title="Session Details" 
        description={`Manage session for ${account.phone || 'Unknown'}`}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeftIcon}
            onClick={() => navigate('/sessions')}
          >
            Back
          </Button>
        }
      />
      
      {error && (
        <Alert 
          variant="error" 
          className="mb-4"
          isDismissible
          onDismiss={() => setError('')}
        >
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert 
          variant="success" 
          className="mb-4"
          isDismissible
          onDismiss={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Account Information">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Phone Number</h4>
              <p className="mt-1 text-sm text-gray-900">{account.phone || 'Not available'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Name</h4>
              <p className="mt-1 text-sm text-gray-900">{account.name || 'Not available'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Status</h4>
              <div className="mt-1">
                {account.status === 'on' ? (
                  <Badge variant="green">Active</Badge>
                ) : account.status === 'off' ? (
                  <Badge variant="gray">Inactive</Badge>
                ) : account.status === 'banned' ? (
                  <Badge variant="red">Banned</Badge>
                ) : account.status === 'pending' ? (
                  <Badge variant="yellow">Pending</Badge>
                ) : (
                  <Badge variant="gray">{account.status || 'Unknown'}</Badge>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Full</h4>
              <p className="mt-1 text-sm text-gray-900">
                {account.full ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Groups</h4>
              <p className="mt-1 text-sm text-gray-900">
                {account.group_count || 0}
              </p>
            </div>
          </div>
        </Card>
        
        <Card title="Session Information">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Session Status</h4>
              <div className="mt-1">
                {session.database_exists && session.files_exists ? (
                  <Badge variant="green">Active</Badge>
                ) : !session.database_exists && !session.files_exists ? (
                  <Badge variant="gray">Not Found</Badge>
                ) : (
                  <Badge variant="yellow">Partial</Badge>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Database Files</h4>
              <div className="mt-1 flex items-center">
                {session.database_exists ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                    <span className="text-sm text-gray-900">Present</span>
                  </>
                ) : (
                  <>
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1" />
                    <span className="text-sm text-gray-900">Missing</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Files Directory</h4>
              <div className="mt-1 flex items-center">
                {session.files_exists ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                    <span className="text-sm text-gray-900">Present</span>
                  </>
                ) : (
                  <>
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1" />
                    <span className="text-sm text-gray-900">Missing</span>
                  </>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Size</h4>
              <p className="mt-1 text-sm text-gray-900">
                {session.size_mb ? `${session.size_mb.toFixed(2)} MB` : 'N/A'}
              </p>
            </div>
            {session.database_path && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Database Path</h4>
                <p className="mt-1 text-sm text-gray-900 break-all">
                  {session.database_path}
                </p>
              </div>
            )}
            {session.files_path && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Files Path</h4>
                <p className="mt-1 text-sm text-gray-900 break-all">
                  {session.files_path}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      <Card title="Session Management" className="mt-6">
        <div className="prose max-w-none mb-6">
          <p>
            You can manage the session files for this account here. Be careful when deleting or resetting sessions, as this can affect the account's functionality.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Warning:</strong> Resetting a session will delete all local data for this account. The account will need to be re-authenticated before it can be used again.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <Button
            variant="danger"
            icon={TrashIcon}
            onClick={handleDeleteSession}
            isLoading={deleteSessionMutation.isLoading}
            disabled={deleteSessionMutation.isLoading || resetSessionMutation.isLoading || (!session.database_exists && !session.files_exists)}
          >
            Delete Session Files
          </Button>
          <Button
            variant="warning"
            icon={ArrowPathIcon}
            onClick={handleResetSession}
            isLoading={resetSessionMutation.isLoading}
            disabled={deleteSessionMutation.isLoading || resetSessionMutation.isLoading || (!session.database_exists && !session.files_exists)}
          >
            Reset Session
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SessionDetail;
